import { useState } from 'react';

import { PaginationState } from '@tanstack/react-table';

import ReconciliationRunDetailsGate from 'app/components/Reconciliations/ReconciliationRunDetailsGate';
import ReconciliationPagedTable from 'app/components/Reconciliations/ReconciliationPagedTable';
import ResolveFindingDialog from 'app/components/Reconciliations/ResolveFindingDialog';

import { defaultPageSize } from 'app/constants/table';

import useFetchReconciliationFindings from 'app/hooks/useFetchReconciliationFindings';

import { ReconciliationFindingAction } from 'app/types/reconciliations';

import getReconciliationFindingColumns from 'app/utils/table/reconciliationFindings';

const EMPTY_TEXT = {
  new: 'This run found no new problems',
  resolved: 'This run resolved no problems',
  past: 'There are no past problems that were never fixed',
  fixed: 'No problems have been fixed or accepted by hand',
};

interface ReconciliationFindingsTabProps {
  reconciliationId: string;
  mode: 'new' | 'resolved' | 'past' | 'fixed';
  companyId?: string;
  isRunning: boolean;
  detailsAvailable: boolean;
}

/**
 * Lists the problems a reconciliation run found for the first time, the ones it resolved, every problem that is past,
 * never fixed (one no run can check any more), or every problem an admin marked as fixed or accepted by hand. New and
 * past problems can be marked as fixed or accepted from here.
 */
function ReconciliationFindingsTab({
  reconciliationId,
  mode,
  companyId = undefined,
  isRunning,
  detailsAvailable,
}: ReconciliationFindingsTabProps) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const isNew = mode === 'new';
  const isPast = mode === 'past';
  const isFixed = mode === 'fixed';
  const [findingToResolve, setFindingToResolve] = useState<
    { id: string; action: ReconciliationFindingAction } | undefined
  >();
  const canShow = !isRunning && detailsAvailable;

  const { data, isLoading, isError } = useFetchReconciliationFindings(
    {
      firstSeenReconciliationId: isNew ? reconciliationId : undefined,
      resolvedReconciliationId: mode === 'resolved' ? reconciliationId : undefined,
      lapsed: isPast || undefined,
      manuallyResolved: isFixed || undefined,
      companyIds: companyId ? [companyId] : undefined,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    },
    canShow
  );

  return (
    <ReconciliationRunDetailsGate
      isRunning={isRunning}
      detailsAvailable={detailsAvailable}
      pendingText="Its problems"
    >
      <>
        <ReconciliationPagedTable
          page={data}
          isLoading={isLoading}
          isError={isError}
          columns={getReconciliationFindingColumns({
            showCompanyColumn: !companyId,
            showResolvedAt: mode === 'resolved' || isFixed,
            showLapsedAt: isPast,
            showResolvedBy: isFixed,
            onResolve:
              isNew || isPast
                ? (finding) => setFindingToResolve({ id: finding.id, action: 'resolve' })
                : undefined,
            onAccept:
              isNew || isPast
                ? (finding) => setFindingToResolve({ id: finding.id, action: 'accept' })
                : undefined,
          })}
          pagination={pagination}
          onPaginationChange={setPagination}
          getRowId={(finding) => finding.id}
          emptyText={EMPTY_TEXT[mode]}
          errorText="Sorry, we failed to fetch these problems. Please try again."
          testId={`reconciliation-${mode}-findings`}
        />
        <ResolveFindingDialog
          findingId={findingToResolve?.id}
          action={findingToResolve?.action}
          onClose={() => setFindingToResolve(undefined)}
        />
      </>
    </ReconciliationRunDetailsGate>
  );
}

export default ReconciliationFindingsTab;
